"use client";

/**
 * Responsive Select Component
 *
 * Uses the Standardized Drawer System for mobile option selection.
 * For comprehensive drawer usage guide, examples, and best practices, see:
 * 📖 docs/ui-components/STANDARDIZED_DRAWER_README.md
 */

import React from "react";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { DesktopSelect } from "./DesktopSelect";
import { MobileSelectDrawer } from "./MobileSelectDrawer";
import { BaseSelectProps } from "./types";

export const ResponsiveSelect: React.FC<BaseSelectProps> = ({
  mobileBreakpoint = "(max-width: 768px)",
  forceSelect = false,
  forceDrawer = false,
  ...props
}) => {
  const isMobile = useMediaQuery(mobileBreakpoint);

  // Determine whether to use drawer or select - ONLY on mobile
  const shouldUseDrawer = React.useMemo(() => {
    if (forceSelect) return false;
    if (forceDrawer) return true;
    // Only use drawer on mobile, not desktop (even with touch)
    return isMobile;
  }, [isMobile, forceSelect, forceDrawer]);

  if (shouldUseDrawer) {
    return <MobileSelectDrawer {...props} />;
  }

  return <DesktopSelect {...props} />;
};

// Export a hook for easier usage with state management
export const useResponsiveSelect = (defaultValue = "") => {
  const [value, setValue] = React.useState(defaultValue);

  const handleValueChange = React.useCallback((newValue: string, option?: any) => {
    setValue(newValue);
  }, []);

  const reset = React.useCallback(() => setValue(defaultValue), [defaultValue]);

  return {
    value,
    setValue,
    handleValueChange,
    reset,
  };
};
